import type { SourceId } from '../../../../constants';
import type {
  DataServiceMediaDocumentType,
  DataServiceMediaImageType,
  DataServiceMediaValueType,
  DataServiceMediaVideoType
} from '../../../types';
import type {
  GalleryMediaValue,
  LinkMediaValue,
  MediaValue,
  PictureValueType
} from '../../sources';
import { getMediaWithProxy } from '../getMediaWithProxy';
import { matchMediaType } from '../matchMediaType';

type DataServicePostMediaValue = Exclude<
  MediaValue<PictureValueType[] | string>,
  LinkMediaValue
>;

const PROVIDERS_WITH_PROXY = ['linkedin', 'instagram', 'facebook', 'tiktok'];

function getImageSizesArray<T extends Partial<DataServiceMediaValueType>>({
  original,
  standard,
  thumbnail,
  cover
}: T) {
  if (cover) {
    return getImageSizesArray(cover);
  }

  return [original, standard, thumbnail].reduce<PictureValueType[]>(
    (acc, pictureValue) => {
      if (!pictureValue?.url) {
        return acc;
      }

      acc.push({
        ...pictureValue,
        url: getMediaWithProxy(pictureValue.url)
      });

      return acc;
    },
    []
  );
}

export function getDataServicePostMedia(
  attachments: DataServiceMediaValueType[],
  platform: SourceId
): DataServicePostMediaValue | undefined {
  if (!attachments?.length) {
    return undefined;
  }

  if (attachments.length > 1) {
    const [firstAttachment] = attachments;

    const attachmentSizes = getImageSizesArray(firstAttachment);

    if (!attachmentSizes.length) {
      return;
    }

    const thumbnail = attachmentSizes.find(({ url }) => !!url)!.url;

    return {
      type: 'gallery',
      items: attachments.map((attachment) =>
        getDataServicePostMedia([attachment], platform)
      ) as GalleryMediaValue['items'],
      thumbnail
    };
  }

  const [{ type, ...mediaProps }] = attachments;

  const mediaType = matchMediaType(type);

  if (mediaType === 'image') {
    const imageProps = mediaProps as DataServiceMediaImageType;

    return {
      type: 'image',
      src: getImageSizesArray(imageProps)
    };
  }

  if (mediaType === 'video') {
    const { url, cover } = mediaProps as DataServiceMediaVideoType;

    const coverSizes = getImageSizesArray(cover || {});

    if (!coverSizes.length || !url) {
      return;
    }

    const thumbnail = coverSizes.find(({ url }) => !!url)!.url;

    return {
      type: 'video',
      src: PROVIDERS_WITH_PROXY.includes(platform)
        ? getMediaWithProxy(url)
        : url,
      thumbnail
    };
  }

  if (type === 'article') {
    const { url, image, title } = mediaProps as DataServiceMediaDocumentType;

    const imageSizes = image ? getImageSizesArray(image) : [];

    if (!imageSizes.length || !url) {
      return;
    }

    const [{ url: src }] = imageSizes;

    return {
      type: 'link',
      description: '',
      title: title || '',
      url: url ?? '',
      src
    };
  }

  if (mediaType === 'gallery') {
    const { images } = mediaProps as DataServiceMediaDocumentType;

    const imageSizes = getImageSizesArray(images[0]);

    if (!imageSizes.length) {
      return;
    }

    const thumbnail = imageSizes.find(({ url }) => !!url)!.url;

    return {
      type: 'gallery',
      items: images.map((image) => ({
        type: 'image',
        src: getImageSizesArray(image)
      })),
      thumbnail
    };
  }

  return;
}
